import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import lt from 'ember-truth-helpers/helpers/lt';
import { practiceLocationIndex } from 'parog-web/cnam-adapters/utils';
import type { PracticeLocation } from 'parog-web/cnam/types';
import type DmpConnectJSAuthenticationContextAdapter from 'parog-web/contexts/dmpconnect-js/authentication-context-adapter';
import Button from 'ui-kit/components/button';
import fuiDropdown from 'wise-fui-modules/modifiers/fui-dropdown';

interface Args {
  context: DmpConnectJSAuthenticationContextAdapter;
  onDismiss: () => void;
}

export default class CnamServicesAccessFormAuthenticationSetPracticeLocationComponent extends Component<Args> {
  @tracked selectedPracticeLocationIndex: number;

  declare availablePracticeLocations: PracticeLocation[];
  isPracticeLocationInvalid = false;
  declare tempPracticeLocation?: string;

  get #practiceLocation(): string | undefined {
    return this.args.context.practiceLocation;
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);
    this.availablePracticeLocations =
      this.args.context.availableLocations ||
      this.args.context.cpsInformations?.practiceLocations ||
      [];

    if (!this.#practiceLocation) {
      this.selectedPracticeLocationIndex = -1;
    } else {
      const index = practiceLocationIndex(
        this.#practiceLocation,
        this.availablePracticeLocations
      );

      this.selectedPracticeLocationIndex = index;

      if (index === -1) {
        this.isPracticeLocationInvalid = true;
      }

      this.tempPracticeLocation = this.#practiceLocation;
    }
  }

  onSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    this.args.context.practiceLocation = this.tempPracticeLocation as string;
    this.tempPracticeLocation = undefined;
    await this.args.context.transitionTo();
  };

  selectPracticeLocation = (newIndex: number): void => {
    this.selectedPracticeLocationIndex = newIndex;
    this.isPracticeLocationInvalid = false;
    this.tempPracticeLocation =
      this.availablePracticeLocations[newIndex].structureName;
  };

  <template>
    <div data-test-practice-locations-state>
      <form
        {{on 'submit' this.onSubmit}}
        class='ui form'
        id='dmp-access-form-practice-location-selection-state-form'
      >

        {{#if this.isPracticeLocationInvalid}}
          <div
            class='ui negative icon message'
            data-test-message='invalidPracticeLocation'
          >
            <i class='times icon'></i>
            <div class='content'>
              <div class='header'>Situation d'exercice invalide</div>
              Votre ancienne situation d'exercice n'est plus valide, veuillez en
              sélectionner une nouvelle.
            </div>
          </div>
        {{/if}}

        <div class='field'>
          <label class='ui small header'>Sélectionnez la situation d'exercice</label>
          {{! prettier-ignore }}
          <div
            class='ui dropdown selection'
            {{fuiDropdown onChange=this.selectPracticeLocation placeholder="Veuillez sélectionner une situation d'exercice" }}
            data-test-dropdown='practice-location'
          >
            <input type='hidden' value={{this.selectedPracticeLocationIndex}} />
            <div class='default text' data-test-select-placeholder>Situation
              d'exercice</div>
            <i class='dropdown icon'></i>
            <div class='menu'>
              {{#each
                this.availablePracticeLocations
                as |practiceLocation index|
              }}
                <div data-value={{index}} class='item'>
                  {{practiceLocation.structureName}}
                </div>
              {{/each}}
            </div>
          </div>
        </div>
        <p>Votre choix sera conservé pour les accès suivants. Vous pourrez
          toujours le modifier.</p>

        <div class='form-buttons'>
          <Button
            type='button'
            class='ui fluid form-button'
            {{on 'click' @onDismiss}}
          >
            Annuler
          </Button>
          <Button
            data-test-button='submit-dmp-access-form'
            type='submit'
            class='ui fluid primary form-button
              {{if (lt this.selectedPracticeLocationIndex 0) "disabled"}}'
          >
            Continuer
          </Button>
        </div>
      </form>
    </div>
  </template>
}
